import { Box, Chip, IconButton, Paper, Typography } from "@mui/material"
import Avatar from "@mui/material/Avatar"
import {
  ChatOutlined,
  ChevronRightOutlined,
  HomeWorkOutlined,
  MailOutlined,
  PhoneIphoneOutlined,
  VisibilityOutlined,
} from "@mui-symbols-material/w300"
import type { ReactElement } from "react"
import React from "react"

import { getPrimaryContactMethods } from "./utils"

import { formatEnumValue } from "@/constants/pricingTiers"
import type { Contact } from "@/graphql/codegen/graphql"
import { formatCounterNumber } from "@/screens/Companies/edit/utils/formatNumber"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

const ContactCard = ({
  contact,
  onCardClick,
  onCommentClick,
  onSubscriptionClick,
  onLocationClick,
}: {
  contact?: Contact
  onCardClick: () => void
  onCommentClick?: () => void
  onSubscriptionClick?: () => void
  onLocationClick?: () => void
}): ReactElement => {
  const {
    firstName,
    lastName,
    isActive,
    companyDepartment,
    contactLocations2 = [],
    physicalLocation,
    contactMethod = [],
    contactSubscriptions = [],
    title = "",
  } = contact ?? {}

  const { email, phone, phoneExt } = getPrimaryContactMethods(contactMethod)

  //@TODO: change contactLocations2 to contactLocations with resolved issue with contactRolesLocation AK-2615
  const renderLocationCounter = () => (
    <Avatar className='rounded bg-gray-400 text-gray-800'>{formatCounterNumber(contactLocations2?.length ?? 0)}</Avatar>
  )

  const renderSubscriptionCounter = () => (
    <Avatar className='rounded bg-tertiary-yellow-900 text-white'>
      {formatCounterNumber(contactSubscriptions.length)}
    </Avatar>
  )

  return (
    <Paper
      elevation={6}
      className='h-auto w-full rounded border p-4 hover:cursor-pointer hover:border-primary-400 sm:h-[255px] sm:w-[341px] sm:p-[16px]'
      onClick={() => onCardClick()}
    >
      <Box className='flex h-full flex-col'>
        <Box className='mb-2 flex items-center justify-between'>
          <Box className='flex max-h-6 w-full items-center'>
            <Typography variant='subtitle1' className='max-w-[80%] truncate text-gray-900' fontWeight='normal'>
              {firstName} {lastName}
            </Typography>
            {!isActive && (
              <Chip
                data-testid='contact-inactive-chip'
                label='Inactive'
                className='ml-2 max-h-6 border-0 bg-red-100 text-xs text-red-600'
                variant='outlined'
                color='error'
              />
            )}
          </Box>

          <IconButton color='primary' size='medium' className='p-0'>
            <VisibilityOutlined />
          </IconButton>
        </Box>
        <Typography className='mb-2 grow truncate text-gray-700' fontWeight='normal' fontSize={12}>
          <span className='font-bold'>{companyDepartment ? `${formatEnumValue(companyDepartment)}` : ""}</span> {title}
        </Typography>
        <Box className='mb-4'>
          {email && (
            <Box className='mb-1 flex items-center gap-2'>
              <MailOutlined fontSize='small' className='text-gray-600' />
              <Typography
                variant='body2'
                className='max-w-[calc(100%-24px)] truncate text-gray-700'
                fontWeight='normal'
              >
                {email}
              </Typography>
            </Box>
          )}
          {phone && (
            <Box className='mb-1 flex items-center gap-2'>
              <PhoneIphoneOutlined fontSize='small' className='text-gray-600' />
              <Typography
                variant='body2'
                className='max-w-[calc(100%-24px)] truncate text-gray-700'
                fontWeight='normal'
              >
                {phone} Ext: {phoneExt}
              </Typography>
            </Box>
          )}
          {physicalLocation && (
            <Box className='flex items-center gap-2'>
              <HomeWorkOutlined fontSize='small' className='text-gray-600' />
              <Typography variant='body2' className='max-w-[calc(100%-24px)] text-gray-700' fontWeight='normal'>
                {formatLocationAddress(physicalLocation, AddressFormat.AddressOnly)}
              </Typography>
            </Box>
          )}
        </Box>
        <Box className='flex flex-wrap items-center gap-2'>
          <Chip
            label='Locations'
            className='rounded-sm font-[12px] text-gray-900'
            onDelete={() => {}}
            avatar={renderLocationCounter()}
            deleteIcon={<ChevronRightOutlined className='text-gray-800' />}
            onClick={(event) => {
              event.stopPropagation()
              onLocationClick?.()
            }}
          />
          <Chip
            label='Subscriptions'
            className='rounded-sm bg-tertiary-yellow-300 font-[12px] text-gray-900'
            onDelete={() => {}}
            avatar={renderSubscriptionCounter()}
            deleteIcon={<ChevronRightOutlined className='text-gray-800' />}
            onClick={(event) => {
              event.stopPropagation()
              onSubscriptionClick?.()
            }}
          />
          <IconButton
            type='button'
            className='size-6 rounded bg-gray-100 text-primary hover:bg-primary-100'
            aria-label='comments'
            onClick={(event) => {
              event.stopPropagation()
              onCommentClick?.()
            }}
          >
            <ChatOutlined />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  )
}

export default ContactCard
